import React, { useEffect, useState } from "react";
import { Formik, Form, Field, ErrorMessage } from "formik";
import * as Yup from "yup";
import {
  FiFileText,
  FiSave,
  FiPlus,
  FiEdit2,
  FiTrash2,
  FiCalendar,
  FiChevronLeft,
  FiChevronRight,
  FiX,
  FiEye,
} from "react-icons/fi";
import ApiServices from "../../services/apiservices";
import { toast } from "sonner";

const FeedersReports = () => {
  const [reports, setReports] = useState([]);
  const [loading, setLoading] = useState(true);

  const [page, setPage] = useState(1);
  const [totalPages, setTotalPages] = useState(1);

  const [selectedYear, setSelectedYear] = useState("All");
  const [selectedMonth, setSelectedMonth] = useState("All");

  const [isModalOpen, setIsModalOpen] = useState(false);
  const [editingReport, setEditingReport] = useState(null);

  const years = [
    "2023",
    "2024",
    "2025",
    "2026",
    "2027",
    "2028",
    "2029",
    "2030",
  ];

  const months = [
    { value: "01", label: "January" },
    { value: "02", label: "February" },
    { value: "03", label: "March" },
    { value: "04", label: "April" },
    { value: "05", label: "May" },
    { value: "06", label: "June" },
    { value: "07", label: "July" },
    { value: "08", label: "August" },
    { value: "09", label: "September" },
    { value: "10", label: "October" },
    { value: "11", label: "November" },
    { value: "12", label: "December" },
  ];

  /**
   * Fetch Reports
   */
  const fetchReports = async () => {
    setLoading(true);

    try {
      const endpoint = `/feedersreports/fetch.php?source=admin&page=${page}&limit=10&year=${selectedYear}&month=${selectedMonth}`;

      const response = await ApiServices.getRequest(endpoint);

      if (response?.success) {
        setReports(response.orders || response.reports || []);
        setTotalPages(response.totalPages || 1);
      } else {
        setReports([]);
      }
    } catch (error) {
      console.error(error);
      toast.error("Failed to fetch feeder reports.");
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchReports();
  }, [page, selectedYear, selectedMonth]);

  /**
   * Open Create Modal
   */
  const openCreateModal = () => {
    setEditingReport(null);
    setIsModalOpen(true);
  };

  /**
   * Open Edit Modal
   */
  const openEditModal = (report) => {
    setEditingReport(report);
    setIsModalOpen(true);
  };

  /**
   * Delete Report
   */
  const handleDelete = async (id) => {
    const confirmed = window.confirm(
      "Are you sure you want to delete this feeder report?"
    );

    if (!confirmed) return;

    try {
      const response = await ApiServices.deleteRequest(
        `/feedersreports/delete.php?id=${id}`
      );

      if (response.success) {
        toast.success(response.message || "Report deleted successfully.");
        fetchReports();
      } else {
        toast.error(response.message);
      }
    } catch (error) {
      toast.error(error.message || "Unable to delete report.");
    }
  };

  /**
   * Validation
   */
  const validationSchema = Yup.object({
    year: Yup.string().required("Year is required"),

    month: Yup.string().required("Month is required"),

    link: Yup.string()
      .url("Please enter a valid URL")
      .required("Excel link is required"),
  });

  /**
   * Save Report
   */
  const handleSubmit = async (
    values,
    { setSubmitting, resetForm }
  ) => {
    try {
      let response;

      if (editingReport) {
        response = await ApiServices.putRequest(
          `/feedersreports/update.php?id=${editingReport.id}`,
          values
        );
      } else {
        console.log("values::", values);
        
        response = await ApiServices.postRequest(
          "/feedersreports/create.php",
          values
        );
      }

      if (response.success) {
        toast.success(
          response.message || "Report saved successfully."
        );

        resetForm();

        setEditingReport(null);

        setIsModalOpen(false);

        fetchReports();
      } else {
        toast.error(response.message);
      }
    } catch (error) {
      toast.error(
        error.message || "An unexpected error occurred."
      );
    } finally {
      setSubmitting(false);
    }
  };
  return (
    <div className="w-full min-h-screen bg-gray-50 py-10">
      <div className="max-w-7xl mx-auto px-4">
  
        {/* Header */}
        <div className="flex flex-col lg:flex-row lg:items-center lg:justify-between gap-4 mb-8">
  
          <div>
            <p className="uppercase text-xs tracking-[0.25em] text-green-700 font-bold">
              Admin Dashboard
            </p>
  
            <h1 className="text-3xl md:text-4xl font-black mt-2">
              Feeders Reports
            </h1>
  
            <p className="text-gray-500 mt-2">
              Create, update and manage Band A feeder reports.
            </p>
          </div>
  
          <button
            onClick={openCreateModal}
            className="bg-green-700 hover:bg-green-800 text-white px-6 py-3 rounded-xl flex items-center gap-2 font-semibold shadow-lg transition"
          >
            <FiPlus />
            New Report
          </button>
  
        </div>
  
        {/* Filters */}
  
        <div className="bg-white rounded-2xl border border-gray-200 shadow-sm p-5 mb-8">
  
          <div className="flex items-center gap-2 mb-5">
  
            <FiCalendar className="text-green-700" />
  
            <span className="font-semibold">
              Filter Reports
            </span>
  
          </div>
  
          <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
  
            <select
              value={selectedYear}
              onChange={(e) => {
                setSelectedYear(e.target.value);
                setPage(1);
              }}
              className="border rounded-xl p-3 outline-none focus:ring-2 focus:ring-green-600"
            >
  
              <option value="All">All Years</option>
  
              {years.map((year) => (
  
                <option
                  key={year}
                  value={year}
                >
                  {year}
                </option>
  
              ))}
  
            </select>
  
            <select
              value={selectedMonth}
              onChange={(e) => {
                setSelectedMonth(e.target.value);
                setPage(1);
              }}
              className="border rounded-xl p-3 outline-none focus:ring-2 focus:ring-green-600"
            >
  
              <option value="All">
                All Months
              </option>
  
              {months.map((month) => (
  
                <option
                  key={month.value}
                  value={month.value}
                >
                  {month.label}
                </option>
  
              ))}
  
            </select>
  
          </div>
  
        </div>
  
        {/* Loading */}
  
        {loading ? (
  
          <div className="bg-white rounded-2xl shadow border p-16">
  
            <div className="flex justify-center">
  
              <div className="w-12 h-12 border-4 border-green-700 border-t-transparent rounded-full animate-spin"></div>
  
            </div>
  
          </div>
  
        ) : reports.length === 0 ? (
  
          <div className="bg-white rounded-2xl border p-16 text-center">
  
            <FiFileText
              size={60}
              className="mx-auto text-gray-300 mb-4"
            />
  
            <h3 className="font-bold text-xl">
              No Reports Found
            </h3>
  
            <p className="text-gray-500 mt-2">
              No feeder reports are available for the selected filters.
            </p>
  
          </div>
  
        ) : (
  
          <div className="bg-white rounded-2xl shadow border overflow-hidden">
  
            <div className="overflow-x-auto">
  
              <table className="w-full">
  
                <thead className="bg-gray-100">
  
                  <tr>
  
                    <th className="px-5 py-4 text-left text-sm font-bold">
                      Year
                    </th>
  
                    <th className="px-5 py-4 text-left text-sm font-bold">
                      Month
                    </th>
  
                    <th className="px-5 py-4 text-left text-sm font-bold">
                      Excel Link
                    </th>
  
                    <th className="px-5 py-4 text-left text-sm font-bold">
                      Created
                    </th>
  
                    <th className="px-5 py-4 text-center text-sm font-bold">
                      Actions
                    </th>
  
                  </tr>
  
                </thead>
  
                <tbody>
  
                  {reports.map((report) => (
  
                    <tr
                      key={report.id}
                      className="border-t hover:bg-gray-50"
                    >
  
                      <td className="px-5 py-4 font-semibold">
  
                        {report.year}
  
                      </td>
  
                      <td className="px-5 py-4">
  
                        {
                          months.find(
                            (m) => m.value === report.month
                          )?.label || report.month
                        }
  
                      </td>
  
                      <td className="px-5 py-4">
  
                        <a
                          href={report.link}
                          target="_blank"
                          rel="noopener noreferrer"
                          className="text-green-700 hover:underline"
                        >
                          Open Excel
                        </a>
  
                      </td>
  
                      <td className="px-5 py-4 text-gray-500">
  
                        {report.createdAt
                          ? new Date(
                              report.createdAt
                            ).toLocaleDateString()
                          : "-"}
  
                      </td>
  
                      <td className="px-5 py-4">
  
                        <div className="flex justify-center gap-2">
  
                          <a
                            href={report.link}
                            target="_blank"
                            rel="noopener noreferrer"
                            className="bg-gray-100 hover:bg-gray-200 p-2 rounded-lg"
                            title="Open"
                          >
                            <FiEye />
                          </a>
  
                          <button
                            onClick={() =>
                              openEditModal(report)
                            }
                            className="bg-blue-50 hover:bg-blue-600 hover:text-white p-2 rounded-lg transition"
                          >
                            <FiEdit2 />
                          </button>
  
                          <button
                            onClick={() =>
                              handleDelete(report.id)
                            }
                            className="bg-red-50 hover:bg-red-600 hover:text-white p-2 rounded-lg transition"
                          >
                            <FiTrash2 />
                          </button>
  
                        </div>
  
                      </td>
  
                    </tr>
  
                  ))}
  
                </tbody>
  
              </table>
  
            </div>
  
          </div>
  
        )}
  
        {/* Pagination */}
  
        {!loading && totalPages > 1 && (
  
          <div className="flex justify-center items-center gap-4 mt-8">
  
            <button
              disabled={page === 1}
              onClick={() =>
                setPage((prev) => prev - 1)
              }
              className="border rounded-xl p-3 disabled:opacity-40 hover:bg-gray-100"
            >
              <FiChevronLeft />
            </button>
  
            <span className="font-semibold">
  
              Page {page} of {totalPages}
  
            </span>
  
            <button
              disabled={page === totalPages}
              onClick={() =>
                setPage((prev) => prev + 1)
              }
              className="border rounded-xl p-3 disabled:opacity-40 hover:bg-gray-100"
            >
              <FiChevronRight />
            </button>
  
          </div>
  
        )}
  
        {/* Modal Starts Here */}
              {/* Modal */}
      {isModalOpen && (
        <div className="fixed inset-0 bg-black/60 backdrop-blur-sm z-50 flex items-center justify-center p-4">

          <div className="bg-white rounded-2xl w-full max-w-xl shadow-2xl relative">

            {/* Header */}

            <div className="flex items-center justify-between border-b px-6 py-5">

              <div>

                <h2 className="text-2xl font-black">

                  {editingReport
                    ? "Edit Feeder Report"
                    : "Create Feeder Report"}

                </h2>

                <p className="text-sm text-gray-500 mt-1">

                  {editingReport
                    ? "Update feeder report information."
                    : "Publish a new feeder report."}

                </p>

              </div>

              <button
                onClick={() => {
                  setEditingReport(null);
                  setIsModalOpen(false);
                }}
                className="text-gray-500 hover:text-red-600"
              >
                <FiX size={24} />
              </button>

            </div>

            <Formik
              enableReinitialize
              initialValues={{
                year: editingReport?.year || "",
                month: editingReport?.month || "",
                link: editingReport?.link || "",
              }}
              validationSchema={validationSchema}
              onSubmit={handleSubmit}
            >
              {({ isSubmitting }) => (

                <Form className="p-6 space-y-5">

                  {/* Year */}

                  <div>

                    <label className="block mb-2 font-semibold">

                      Year

                    </label>

                    <Field
                      as="select"
                      name="year"
                      className="w-full border rounded-xl p-3 focus:ring-2 focus:ring-green-600 outline-none"
                    >

                      <option value="">
                        Select Year
                      </option>

                      {years.map((year) => (

                        <option
                          key={year}
                          value={year}
                        >
                          {year}
                        </option>

                      ))}

                    </Field>

                    <ErrorMessage
                      name="year"
                      component="div"
                      className="text-red-500 text-sm mt-1"
                    />

                  </div>

                  {/* Month */}

                  <div>

                    <label className="block mb-2 font-semibold">

                      Month

                    </label>

                    <Field
                      as="select"
                      name="month"
                      className="w-full border rounded-xl p-3 focus:ring-2 focus:ring-green-600 outline-none"
                    >

                      <option value="">
                        Select Month
                      </option>

                      {months.map((month) => (

                        <option
                          key={month.value}
                          value={month.value}
                        >
                          {month.label}
                        </option>

                      ))}

                    </Field>

                    <ErrorMessage
                      name="month"
                      component="div"
                      className="text-red-500 text-sm mt-1"
                    />

                  </div>

                  {/* Excel Link */}

                  <div>

                    <label className="block mb-2 font-semibold">

                      Excel File Link

                    </label>

                    <Field
                      name="link"
                      type="url"
                      placeholder="https://example.com/report.xlsx"
                      className="w-full border rounded-xl p-3 focus:ring-2 focus:ring-green-600 outline-none"
                    />

                    <ErrorMessage
                      name="link"
                      component="div"
                      className="text-red-500 text-sm mt-1"
                    />

                  </div>

                  {/* Buttons */}

                  <div className="flex justify-end gap-3 pt-3">

                    <button
                      type="button"
                      onClick={() => {
                        setEditingReport(null);
                        setIsModalOpen(false);
                      }}
                      className="px-5 py-3 rounded-xl border font-semibold hover:bg-gray-100"
                    >
                      Cancel
                    </button>

                    <button
                      type="submit"
                      disabled={isSubmitting}
                      className="bg-green-700 hover:bg-green-800 text-white px-6 py-3 rounded-xl flex items-center gap-2 disabled:opacity-50"
                    >

                      <FiSave />

                      {isSubmitting
                        ? "Saving..."
                        : editingReport
                        ? "Update Report"
                        : "Create Report"}

                    </button>

                  </div>

                </Form>

              )}

            </Formik>

          </div>

        </div>
      )}

    </div>

  </div>
);

};

export default FeedersReports;